/**
 * Utility functions for React Query hooks
 */

/**
 * Combines a feature flag check with an external enabled option.
 * This is a common pattern across all query hooks.
 *
 * @param featureEnabled - Internal feature flag (e.g., bingoEnabled, subscriptionEnabled)
 * @param externalEnabled - External enabled option passed from component
 * @returns Combined enabled state
 *
 * @example
 * const enabled = combineEnabled(bingoStore.bingoEnabled, options?.enabled);
 */
export const combineEnabled = (featureEnabled: boolean, externalEnabled?: unknown): boolean => {
  // If externalEnabled is a function, we can't combine it - just use it as-is
  if (typeof externalEnabled === 'function') {
    return featureEnabled;
  }
  return featureEnabled && (externalEnabled !== undefined ? Boolean(externalEnabled) : true);
};

/**
 * Extracts the enabled option from query options and returns it separately.
 * This helps with the common pattern of destructuring options.
 *
 * @param options - Query options object
 * @returns Object with enabled and rest of options
 *
 * @example
 * const { enabled, restOptions } = extractEnabled(options);
 */
export const extractEnabled = <T extends { enabled?: unknown }>(
  options?: T,
): { enabled?: unknown; restOptions: Omit<T, 'enabled'> } => {
  if (!options) {
    return { enabled: undefined, restOptions: {} as Omit<T, 'enabled'> };
  }

  const { enabled, ...restOptions } = options;
  return { enabled, restOptions };
};
